import React, { useState } from "react";
import { Trans } from "@lingui/macro";

import "./month-calendar.scss";
import classNames from "classnames";
import { connectRefinementList } from "react-instantsearch-dom";
import {
  getYears,
  toYears,
  generateRange,
  getStartDate,
  getMonthFromDate,
  getDateFromMonth,
  getMonth
} from "./utils";
import DateOnlyFormat from "components/DateOnlyFormat";
import { useFirstLastDepartureCruise } from "./useFirstLastDepartureCruise";
import { Icon } from "@silversea-ssc/ui-components-react";

interface Props {
  startMonth: string;
  endMonth: string;
  update: (value: string[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any[];
  values: string[];
}

const monthsInOneYear = 12;

const MonthCalendar = ({
  startMonth,
  endMonth,
  items,
  update,
  values
}: Props) => {
  const yearsToShow = getYears(startMonth, endMonth);
  const [visibleYear, setVisibleYear] = useState(yearsToShow[0]);
  const selectedMonths = values;
  const selectedYears = toYears(selectedMonths);

  return (
    <div className="ssc-month-calendar">
      <header className="ssc-month-calendar__header">
        {yearsToShow.map((year) => (
          <div key={year} className="ssc-month-calendar__year-container">
            <input
              type="radio"
              name="year"
              value={year}
              id={`year-${year}`}
              className="ssc-month-calendar__year-input"
              checked={year === visibleYear}
              onChange={(e) => setVisibleYear(parseInt(e.target.value))}
            />
            <label
              htmlFor={`year-${year}`}
              className={classNames("ssc-month-calendar__year", {
                "ssc-month-calendar__year--selected": selectedYears.includes(
                  year
                )
              })}
            >
              {year}
            </label>
          </div>
        ))}
      </header>
      <div className="ssc-month-calendar__months">
        {generateRange(monthsInOneYear).map((monthIndex) => {
          const month = getMonth(visibleYear, monthIndex);
          const resultsInfo = items.find((i) => i.label == month);
          const isSelected = !!resultsInfo && resultsInfo.isRefined;
          const resultsCounter = resultsInfo && resultsInfo.count;
          const isSelectable = resultsCounter > 0;

          const id = `month-${month}`;
          return (
            <div key={month} className="ssc-month-calendar__month-container">
              <input
                type="checkbox"
                name={id}
                id={id}
                className="ssc-month-calendar__month-input"
                disabled={!isSelectable}
                checked={isSelected}
                value={month}
                onChange={(e) =>
                  update(
                    e.target.checked
                      ? [...selectedMonths, e.target.value]
                      : selectedMonths.filter((y) => y !== e.target.value)
                  )
                }
              />
              <label htmlFor={id} className="ssc-month-calendar__month">
                <DateOnlyFormat
                  value={getDateFromMonth(monthIndex)}
                  format={{
                    month: "short"
                  }}
                />
                {resultsCounter && <span> ({resultsCounter})</span>}
              </label>
            </div>
          );
        })}
      </div>

      {selectedMonths.length > 0 && (
        <footer className="ssc-month-calendar__footer">
          <h4 className="ssc-month-calendar__footer-title">
            <Trans>Selected months</Trans>
          </h4>
          <div className="ssc-month-calendar__selections">
            {selectedMonths.map((month) => (
              <button
                className="ssc-month-calendar__selection"
                key={month}
                onClick={(_e) =>
                  update(selectedMonths.filter((y) => y !== month))
                }
              >
                <DateOnlyFormat
                  value={getStartDate(month)}
                  format={{
                    year: "numeric",
                    month: "short"
                  }}
                />
                <Icon name="close" className="icon" padding={false} />
              </button>
            ))}
          </div>
          <button
            className="ssc-month-calendar__selection-clear-all"
            onClick={(_e) => update([])}
          >
            <Trans>Clear selection</Trans>
          </button>
        </footer>
      )}
    </div>
  );
};

const AlgoliaMonthCalendar = ({ items, currentRefinement, refine }) => {
  const today = new Date();
  const startMonth = getMonthFromDate(today);
  const endDate = new Date(today.setFullYear(today.getFullYear() + 2, 11));
  const endMonth = getMonthFromDate(endDate);

  const { minYearMonth, maxYearMonth } = useFirstLastDepartureCruise();

  return (
    <MonthCalendar
      startMonth={minYearMonth || startMonth}
      endMonth={maxYearMonth || endMonth}
      items={items}
      values={currentRefinement}
      update={(selectedValue) => {
        refine(selectedValue);
      }}
    />
  );
};

const ConnectedMonthCalendar = connectRefinementList(AlgoliaMonthCalendar);
export default ConnectedMonthCalendar;
